import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";
import { SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { CuisineComboboxMulti } from "@/components/cuisine-combobox";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChefCard } from "@/components/chef-card";
import { REGIONS } from "@/lib/chef-taxonomy";
import { chefQueries } from "@/lib/chefs.queries";

const searchSchema = z.object({
  city: z.string().optional(),
  cuisine: z.string().optional(),
  budget: z.number().optional(),
  region: z.string().optional(),
  verified: z.boolean().optional(),
});

export const Route = createFileRoute("/search")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Trova chef privati in Italia — ChefConnect" },
      { name: "description", content: "Cerca chef privati in Italia per città, cucina, budget e disponibilità. Profili verificati." },
      { property: "og:title", content: "Trova il tuo chef — ChefConnect" },
      { property: "og:description", content: "Filtra per città, cucina e budget. Tutti i chef privati in Italia." },
      { property: "og:url", content: "https://chefconnectitalia.com/search" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://chefconnectitalia.com/__l5e/assets-v1/a2ae4b98-4144-41f7-845e-72f224ca696b/og-home.jpg" },
      { name: "twitter:image", content: "https://chefconnectitalia.com/__l5e/assets-v1/a2ae4b98-4144-41f7-845e-72f224ca696b/og-home.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://chefconnectitalia.com/search" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(chefQueries.list(200)),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl">Impossibile caricare la ricerca</h1>
      <p className="mt-3 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: SearchPage,
});

const EVENT_TYPES = ["Cena privata", "Compleanno", "Matrimonio", "Aziendale", "Lezione"];

function SearchPage() {
  const search = Route.useSearch();
  const { data: chefs } = useSuspenseQuery(chefQueries.list(200));

  const [city, setCity] = useState(search.city ?? "");
  const [region, setRegion] = useState(search.region ?? "");
  const [cuisines, setCuisines] = useState<Set<string>>(new Set(search.cuisine ? [search.cuisine] : []));
  const [eventType, setEventType] = useState<string>("");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 300]);
  const [verifiedOnly, setVerifiedOnly] = useState(search.verified ?? false);

  const toggleCuisine = (c: string) => {
    setCuisines((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
  };

  const results = useMemo(() => {
    return chefs.filter((chef) => {
      if (city && !(chef.city ?? "").toLowerCase().includes(city.toLowerCase())) return false;
      if (region && chef.region !== region) return false;
      if (cuisines.size > 0 && !chef.cuisines.some((c) => cuisines.has(c))) return false;
      if (verifiedOnly && !chef.verified_badge) return false;
      const priceFrom = chef.min_price ?? chef.hourly_rate ?? 0;
      if (priceFrom < priceRange[0] || priceFrom > priceRange[1]) return false;
      return true;
    });
  }, [chefs, city, region, cuisines, verifiedOnly, priceRange]);

  const resetFilters = () => {
    setCity(""); setRegion(""); setCuisines(new Set()); setEventType("");
    setPriceRange([0, 300]); setVerifiedOnly(false);
  };

  return (
    <div className="container mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
      <div className="mb-10">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Ricerca</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">Trova il tuo chef</h1>
        <p className="mt-3 text-muted-foreground">
          {results.length} chef {results.length === 1 ? "trovato" : "trovati"} che corrispondono ai tuoi criteri.
        </p>
      </div>

      <div className="grid gap-10 lg:grid-cols-[280px_1fr]">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="flex items-center justify-between border-b border-border pb-4">
            <h2 className="inline-flex items-center gap-2 font-display text-lg">
              <SlidersHorizontal className="h-4 w-4 text-gold" /> Filtri
            </h2>
            <button onClick={resetFilters} className="inline-flex items-center gap-1 text-xs text-muted-foreground transition hover:text-foreground">
              <X className="h-3 w-3" /> Reset
            </button>
          </div>

          <div className="mt-6 space-y-7">
            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Città</Label>
              <Input aria-label="Filtra per città" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Es. Milano" className="mt-2" />
            </div>

            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Regione</Label>
              <Select value={region || "__all"} onValueChange={(v) => setRegion(v === "__all" ? "" : v)}>
                <SelectTrigger aria-label="Filtra per regione" className="mt-2 w-full">
                  <SelectValue placeholder="Tutte le regioni" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Tutte le regioni</SelectItem>
                  {REGIONS.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Prezzo a persona · €{priceRange[0]} – €{priceRange[1]}
              </Label>
              <Slider value={priceRange} onValueChange={(v) => setPriceRange([v[0], v[1]] as [number, number])} min={0} max={300} step={10} className="mt-4" />
            </div>

            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Tipologia evento</Label>
              <Select value={eventType || "__any"} onValueChange={(v) => setEventType(v === "__any" ? "" : v)}>
                <SelectTrigger aria-label="Filtra per tipologia evento" className="mt-2 w-full">
                  <SelectValue placeholder="Qualsiasi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Qualsiasi</SelectItem>
                  {EVENT_TYPES.map((e) => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border bg-secondary/40 p-3">
              <Checkbox checked={verifiedOnly} onCheckedChange={(v) => setVerifiedOnly(v === true)} />
              <span className="text-sm">Solo chef verificati</span>
            </label>

            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Tipo di cucina</Label>
              <div className="mt-2">
                <CuisineComboboxMulti
                  value={Array.from(cuisines)}
                  onChange={(arr) => setCuisines(new Set(arr))}
                  placeholder="Tutte le cucine"
                  ariaLabel="Filtra per tipo di cucina"
                />
              </div>
            </div>
          </div>
        </aside>

        <div>
          {chefs.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card/50 px-6 py-20 text-center">
              <h3 className="font-display text-2xl">Nessuno chef disponibile in questa zona</h3>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                Stiamo costruendo la rete di ChefConnect. Torna presto, oppure diventa il primo chef della tua città.
              </p>
              <Button asChild className="mt-6 bg-gold text-gold-foreground hover:bg-gold/90">
                <Link to="/diventa-chef">Diventa Chef</Link>
              </Button>
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card/50 px-6 py-20 text-center">
              <h3 className="font-display text-2xl">Nessuno chef trovato con questi filtri.</h3>
              <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                <li>• Prova ad <strong className="text-foreground">ampliare la zona</strong> o cambiare regione.</li>
                <li>• <strong className="text-foreground">Rimuovi un filtro</strong> (prezzo o solo verificati).</li>
                <li>• Prova una <strong className="text-foreground">categoria diversa</strong>.</li>
              </ul>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                <Button onClick={resetFilters} className="bg-gold text-gold-foreground hover:bg-gold/90">
                  Mostra tutti gli chef
                </Button>
                <Button onClick={resetFilters} variant="outline">Reimposta filtri</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {results.map((chef) => (<ChefCard key={chef.user_id} chef={chef} />))}
              </div>
              <SearchImpressions chefIds={results.map((c) => c.user_id)} city={search.city} cuisine={search.cuisine} />
              <p className="mt-10 text-center text-sm text-muted-foreground">
                Non trovi quello che cerchi? <Link to="/" className="text-foreground underline underline-offset-4">Torna alla home</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Registra 1 search_impression per ogni chef visibile.
// Dedup server-side: max 1 evento/chef/visitatore/giorno → refresh e cambio filtri non gonfiano i numeri.
function SearchImpressions({ chefIds, city, cuisine }: { chefIds: string[]; city?: string; cuisine?: string }) {
  useEffect(() => {
    if (chefIds.length === 0) return;
    const ids = chefIds.slice(0, 30); // cap difensivo
    import("@/lib/analytics").then(({ track }) => {
      for (const id of ids) track("search_impression", id, { city, category: cuisine });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chefIds.join(","), city, cuisine]);
  return null;
}